import { useEffect, useRef, useState, useCallback } from "react";

export interface InactivityConfig {
	timeout: number; // milliseconds
	events?: string[]; // DOM events to track
	onInactive?: () => void;
	onActive?: () => void;
	includeMouseMovement?: boolean;
	includeKeyboard?: boolean;
	includeScroll?: boolean;
	includeTouch?: boolean;
	includeVisibilityChange?: boolean;
	inactiveOnTabBlur?: boolean; // Mark as inactive immediately when tab loses focus
}

export interface InactivityState {
	isInactive: boolean;
	lastActivityTime: number;
	timeSinceLastActivity: number;
}

const DEFAULT_EVENTS = [
	"mousedown",
	"mousemove",
	"keypress",
	"scroll",
	"touchstart",
	"click",
	"keydown",
	"keyup",
	"wheel",
	"contextmenu"
];

export const useInactivityTracker = (config: InactivityConfig): InactivityState => {
	const {
		timeout = 300000, // 5 minutes default
		events = DEFAULT_EVENTS,
		onInactive,
		onActive,
		includeMouseMovement = true,
		includeKeyboard = true,
		includeScroll = true,
		includeTouch = true,
		includeVisibilityChange = true,
		inactiveOnTabBlur = true // Default to true - mark inactive when tab loses focus
	} = config;

	const [isInactive, setIsInactive] = useState<boolean>(false);
	const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now());
	const [timeSinceLastActivity, setTimeSinceLastActivity] = useState<number>(0);

	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const lastActivityRef = useRef<number>(Date.now());
	const isInactiveRef = useRef<boolean>(false);

	const resetTimer = useCallback(() => {
		const now = Date.now();
		const wasInactive = isInactiveRef.current;

		lastActivityRef.current = now;
		setLastActivityTime(now);
		setTimeSinceLastActivity(0);

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			isInactiveRef.current = true;
			setIsInactive(true);
			setTimeSinceLastActivity(timeout);
			onInactive?.();
		}, timeout);

		// If user was inactive and now became active
		if (wasInactive) {
			setIsInactive(false);
			onActive?.();
		}
	}, [timeout, onInactive, onActive]);

	const handleActivity = useCallback(
		(event: Event) => {
			// Filter events based on configuration
			const eventType = event.type;

			if (!includeMouseMovement && eventType === "mousemove") return;
			if (!includeKeyboard && (eventType === "keypress" || eventType === "keydown" || eventType === "keyup")) return;
			if (!includeScroll && (eventType === "scroll" || eventType === "wheel")) return;
			if (!includeTouch && eventType.startsWith("touch")) return;

			resetTimer();
		},
		[resetTimer, includeMouseMovement, includeKeyboard, includeScroll, includeTouch]
	);

	const handleVisibilityChange = useCallback(() => {
		if (!includeVisibilityChange) return;

		if (document.hidden) {
			// Page became hidden
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			if (inactiveOnTabBlur) {
				// Immediately mark as inactive when tab loses focus
				isInactiveRef.current = true;
				setIsInactive(true);
				setTimeSinceLastActivity(timeout);
				onInactive?.();
			}
		} else {
			// Page became visible, resume tracking
			resetTimer();
		}
	}, [resetTimer, includeVisibilityChange, timeout, onInactive, inactiveOnTabBlur]);

	// Update time since last activity
	useEffect(() => {
		const interval = setInterval(() => {
			if (!isInactiveRef.current && !document.hidden) {
				const now = Date.now();
				const timeSince = now - lastActivityRef.current;
				setTimeSinceLastActivity(timeSince);
			}
		}, 1000);

		return () => clearInterval(interval);
	}, []);

	// Set up event listeners
	useEffect(() => {
		// Add event listeners for user activity
		events.forEach((eventType) => {
			document.addEventListener(eventType, handleActivity, { passive: true });
		});

		// Add visibility change listener
		if (includeVisibilityChange) {
			document.addEventListener("visibilitychange", handleVisibilityChange);
		}

		// Initialize timer
		resetTimer();

		// Cleanup function
		return () => {
			events.forEach((eventType) => {
				document.removeEventListener(eventType, handleActivity);
			});

			if (includeVisibilityChange) {
				document.removeEventListener("visibilitychange", handleVisibilityChange);
			}

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [events, includeVisibilityChange]);

	return {
		isInactive,
		lastActivityTime,
		timeSinceLastActivity
	};
};

// Utility function to format time since last activity
export const formatTimeSinceActivity = (milliseconds: number): string => {
	const seconds = Math.floor(milliseconds / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);

	if (hours > 0) {
		return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
	} else if (minutes > 0) {
		return `${minutes}m ${seconds % 60}s`;
	} else {
		return `${seconds}s`;
	}
};

// Utility function to check if user is idle for a specific duration
export const isIdleFor = (timeSinceLastActivity: number, duration: number): boolean => {
	return timeSinceLastActivity >= duration;
};
